(function initializeVoyadoLandingPage(window, document) {
    'use strict';

    const collection = document.getElementById('collection-ajax');
    const grid = document.getElementById('product-grid-ajax');
    const pagination = document.getElementById('pagination-ajax');
    const actions = collection?.querySelector('.product-grid-actions');
    const count = document.getElementById('products-count-ajax');
    const applyCount = document.getElementById('apply-count');
    const recommendations = document.getElementById(
        'VoyadoLandingPageRecommendations'
    );
    const recommendationArrowsTemplate = document.getElementById(
        'VoyadoRecommendationArrows'
    );
    const gridFallbackTemplate = document.getElementById(
        'VoyadoShopifyGridFallback'
    );
    const paginationFallbackTemplate = document.getElementById(
        'VoyadoShopifyPaginationFallback'
    );
    const landingPage = window.VOYADO_LANDING_PAGE;
    const cardLoader = window.VOYADO_PRODUCT_CARD_LOADER;
    const fallbackAssets =
        window.VOYADO_CONTEXT?.landingPage?.shopifyFallback ?? {};

    if (
        !collection ||
        !grid ||
        !landingPage ||
        !cardLoader ||
        !window.VOYADO_LIST_CONTROLS
    ) {
        return;
    }

    let pageState = landingPage.getState();
    let renderId = 0;
    let recommendationRenderId = 0;
    let paginationObserver = null;
    let listControls = null;
    let fallbackInitialized = false;
    const loadingState = grid
        ?.querySelector('[data-voyado-loading]')
        ?.cloneNode(true);

    function getListControls() {
        listControls ||= window.VOYADO_LIST_CONTROLS.create({
            getFilters: () => pageState.request.filters,
            onSortChange: (sort) => landingPage.setSort(sort),
            onFiltersChange: (filters) => landingPage.setFilters(filters),
            onClearFilters: () => landingPage.clearFilters(),
        });
        return listControls;
    }

    function loadScript(src) {
        if (!src || document.querySelector(`script[src="${src}"]`)) return;
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        document.body.append(script);
    }

    function initializeShopifyFallback() {
        collection.classList.remove('processing');
        if (fallbackInitialized) return;
        fallbackInitialized = true;
        grid.removeAttribute('aria-busy');
        grid.replaceChildren(
            gridFallbackTemplate?.content.cloneNode(true) ||
                document.createDocumentFragment()
        );
        pagination.replaceChildren(
            paginationFallbackTemplate?.content.cloneNode(true) ||
                document.createDocumentFragment()
        );
        pagination.hidden = false;
        if (actions) actions.hidden = false;
        loadScript(fallbackAssets.facetsAssetUrl);
        loadScript(fallbackAssets.paginationAssetUrl);
    }

    function showLoadingState() {
        collection.classList.add('processing');
        grid.setAttribute('aria-busy', 'true');

        if (pageState.loadedProductGroups.length === 0 && loadingState) {
            grid.replaceChildren(loadingState.cloneNode(true));
            pagination.hidden = true;
            if (actions) actions.hidden = true;
        }
    }

    function element(tag, className, text) {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text !== undefined) node.textContent = text;
        return node;
    }

    function totalHits() {
        return Number(pageState.response?.primaryList?.totalHits) || 0;
    }

    function countText(total) {
        const one = window.themeStrings?.product_count_simple_one || '[count] product';
        const many = window.themeStrings?.product_count_simple_other || '[count] products';
        return (total === 1 ? one : many).replace('[count]', total);
    }

    function cardRequestUrl(link) {
        try {
            const url = new URL(link, window.location.origin);
            if (url.origin !== window.location.origin) return null;
            url.searchParams.set('view', 'ajax-card');
            return url.href;
        } catch {
            return null;
        }
    }

    async function fetchCard(product) {
        const requestUrl = cardRequestUrl(product?.link);
        if (!requestUrl) return null;

        if (!cardCache.has(requestUrl)) {
            cardCache.set(
                requestUrl,
                fetch(requestUrl, { credentials: 'same-origin' })
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`Card request failed: ${response.status}`);
                        }
                        return response.text();
                    })
                    .then((html) => {
                        const template = document.createElement('template');
                        template.innerHTML = html.trim();
                        return template.content.firstElementChild;
                    })
                    .catch(() => null)
            );
        }

        const cachedCard = await cardCache.get(requestUrl);
        if (!cachedCard) return null;

        const card = cachedCard.cloneNode(true);
        card.dataset.voyadoResultType = 'product';

        if (window.VOYADO_NOTIFICATIONS) {
            card.dataset.voyadoTicket = product.ticket || '';
            card.dataset.voyadoVariantTickets = JSON.stringify(
                Object.fromEntries(
                    (product.variants || [])
                        .filter((variant) => variant?.key && variant?.ticket)
                        .map((variant) => [
                            String(variant.key).trim(),
                            String(variant.ticket).trim(),
                        ])
                )
            );
        }

        if (product.key) {
            for (const link of card.querySelectorAll('a[href]')) {
                const href = link.getAttribute('href');
                if (!href || href.startsWith('#')) continue;
                const url = new URL(link.href, window.location.origin);
                if (url.origin !== window.location.origin) continue;
                url.searchParams.set(PDP_PRODUCT_KEY_PARAMETER, product.key);
                link.href = url.toString();
            }
        }

        return card;
    }

    function recommendationTrack(cards, isCarousel) {
        const iprMobile = recommendations?.dataset.iprMobile || '1';
        const iprTablet = recommendations?.dataset.iprTablet || '3';
        const iprDesktop = recommendations?.dataset.iprDesktop || '4';
        const track = element(
            'div',
            `grid sm-gap${isCarousel ? ' gridy-track scrollbar--hide track-inner-padding' : ''} grid-cols-${iprMobile} sm-grid-cols-${iprTablet} md-grid-cols-${iprDesktop}`
        );
        track.style.setProperty('--gap', recommendations?.dataset.gap || '16px');
        track.style.setProperty('--sm-gap', recommendations?.dataset.smGap || '24px');
        track.append(...cards);
        return track;
    }

    function recommendationSlider(cards) {
        // The theme's gridy-slider custom element reads its children in the
        // constructor, so it cannot safely be assembled with createElement().
        // Use the same track/arrows contract and scrolling behaviour here.
        const slider = element('div', 'gridy-slider voyado-recommendation-slider');
        const iprMobile = recommendations?.dataset.iprMobile || '1';
        const iprTablet = recommendations?.dataset.iprTablet || '3';
        const iprDesktop = recommendations?.dataset.iprDesktop || '4';

        slider.style.position = 'relative';
        slider.style.display = 'block';
        slider.dataset.mobileEnabled = 'true';
        slider.dataset.tabletEnabled = 'true';
        slider.dataset.desktopEnabled = 'true';
        slider.dataset.iprMobile = iprMobile;
        slider.dataset.iprTablet = iprTablet;
        slider.dataset.iprDesktop = iprDesktop;
        slider.dataset.arrows = 'true';
        slider.dataset.slidingBehavior = 'all';
        slider.append(recommendationTrack(cards, true));

        const arrows = recommendationArrowsTemplate?.content
            .firstElementChild
            ?.cloneNode(true);
        if (arrows) {
            if (cards.length <= Number(iprMobile)) arrows.classList.add('sm-down-hide');
            if (cards.length <= Number(iprTablet)) arrows.classList.add('sm-to-md-hide');
            if (cards.length <= Number(iprDesktop)) arrows.classList.add('md-hide');
            slider.append(arrows);
            arrows.addEventListener('click', (event) => {
                const button = event.target.closest('[data-gridy-arrow]');
                if (!button) return;

                const track = slider.querySelector('.gridy-track');
                const direction = button.dataset.direction === 'prev' ? -1 : 1;
                const atStart = track.scrollLeft <= 1;
                const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
                let left = track.scrollLeft + (direction * track.clientWidth);

                if (direction > 0 && atEnd) left = 0;
                if (direction < 0 && atStart) left = track.scrollWidth - track.clientWidth;
                track.scrollTo({ left, behavior: 'smooth' });
            });
        }

        return slider;
    }

    async function renderRecommendations(response) {
        if (!recommendations) return;

        const activeRender = ++recommendationRenderId;
        const lists = Array.isArray(response?.recommendationLists)
            ? response.recommendationLists.filter(
                (list) => list?.visible !== false &&
                    Array.isArray(list?.productGroups) &&
                    list.productGroups.length > 0
            )
            : [];
        const renderedLists = await Promise.all(lists.map(async (list) => {
            const products = list.productGroups
                .map((group) => group?.products?.[0])
                .filter(Boolean);
            const cards = (await Promise.all(products.map(fetchCard))).filter(Boolean);
            if (!cards.length) return null;

            const section = element(
                'section',
                `voyado-recommendation-list container pt pb sm-pt sm-pb color-${recommendations.dataset.colorScheme || 'default'}`
            );
            section.dataset.voyadoRecommendationList = list.id || '';
            section.style.setProperty('--pt', '40px');
            section.style.setProperty('--pb', '40px');
            section.style.setProperty('--sm-pt', '40px');
            section.style.setProperty('--sm-pb', '40px');

            if (list.label) {
                const header = element(
                    'div',
                    'section-header mb-gutter col-sm-max-6 text-align-center'
                );
                header.style.marginInline = 'auto';
                header.append(element('h2', 'mb-4', list.label));
                section.append(header);
            }

            section.append(
                list.visualization === 'GRID'
                    ? recommendationTrack(cards, false)
                    : recommendationSlider(cards)
            );
            return section;
        }));

        if (activeRender !== recommendationRenderId) return;
        const visibleLists = renderedLists.filter(Boolean);
        recommendations.replaceChildren(...visibleLists);
        recommendations.hidden = visibleLists.length === 0;
    }

    function pageUrl(page) {
        const url = new URL(window.location.href);
        if (page > 1) url.searchParams.set('page', String(page));
        else url.searchParams.delete('page');
        return `${url.pathname}${url.search}${url.hash}`;
    }

    function pageLink(page, current) {
        if (page === current) {
            const item = element('span', 'pagination-item current-page', String(page));
            item.setAttribute('aria-current', 'page');
            item.dataset.voyadoPage = String(page);
            return item;
        }

        const item = element('a', 'pagination-item page-link', String(page));
        item.href = pageUrl(page);
        item.dataset.voyadoPage = String(page);
        item.setAttribute(
            'aria-label',
            (window.VOYADO_CONTEXT.labels?.page || 'Page [page]').replace('[page]', page)
        );
        return item;
    }

    function arrowLink(direction, page, disabled) {
        const className = `pagination-item arrow-link arrow-link--${direction}`;
        const item = disabled ? element('span', `${className} disabled`) : element('a', className);
        item.dataset.voyadoPage = String(page);
        item.setAttribute(
            'aria-label',
            window.VOYADO_CONTEXT.labels?.[direction === 'prev' ? 'previous' : 'next'] ||
                (direction === 'prev' ? 'Previous' : 'Next')
        );
        item.innerHTML = direction === 'prev'
            ? '<svg role="presentation" aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="12 6 8 10 12 14"/></svg>'
            : '<svg role="presentation" aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="8 14 12 10 8 6"/></svg>';
        if (!disabled) item.href = pageUrl(page);
        return item;
    }

    function renderPagination() {
        if (!pagination) return;
        paginationObserver?.disconnect();

        const pages = Math.max(1, Math.ceil(totalHits() / pageState.request.limit));
        const current = pageState.page || 1;
        pagination.hidden = pages <= 1;

        if (pages <= 1) {
            pagination.replaceChildren();
            return;
        }

        const type = pagination.dataset.voyadoPaginationType;
        if (type === 'button' || type === 'infinite') {
            const wrapper = element('div', 'pagination mt-48 mb-48');
            const button = element(
                'button',
                'btn btn--secondary size--small',
                window.VOYADO_CONTEXT.labels?.loadMore || 'Load more'
            );
            button.type = 'button';
            button.dataset.voyadoLoadMore = 'true';
            button.dataset.voyadoPage = String(current + 1);
            button.disabled = !pageState.hasMore;
            wrapper.append(button);
            pagination.replaceChildren(wrapper);

            if (type === 'infinite' && pageState.hasMore) {
                paginationObserver = new IntersectionObserver((entries) => {
                    if (entries.some((entry) => entry.isIntersecting)) {
                        landingPage.loadMore();
                    }
                }, { threshold: 0.5 });
                paginationObserver.observe(button);
            }
            return;
        }

        const nav = element('nav', `pagination mt-48 mb-48${pages > 5 ? ' wide-parts' : ''} size--tiny`);
        nav.setAttribute('aria-label', window.VOYADO_CONTEXT.labels?.pagination || 'Pagination');
        const parts = element('div', 'pagination-parts');
        const start = Math.max(1, Math.min(current - 2, pages - 4));
        const end = Math.min(pages, start + 4);

        for (let page = start; page <= end; page += 1) {
            parts.append(pageLink(page, current));
        }

        nav.append(
            arrowLink('prev', current - 1, current === 1),
            parts,
            arrowLink('next', current + 1, current === pages)
        );
        pagination.replaceChildren(nav);
    }

    async function render() {
        if (pageState.status === 'loading') {
            showLoadingState();
            return;
        }
        if (pageState.status === 'fallback') {
            initializeShopifyFallback();
            renderRecommendations(pageState.response);
            return;
        }
        if (pageState.status === 'error') {
            initializeShopifyFallback();
            renderRecommendations(null);
            return;
        }
        if (pageState.status !== 'success') return;

        const activeRender = ++renderId;
        const products = pageState.loadedProductGroups
            .map((group) => group?.products?.[0])
            .filter(Boolean);
        const cards = await Promise.all(
            products.map((product) => cardLoader.load(product))
        );
        if (activeRender !== renderId) return;

        grid.replaceChildren(...cards.filter(Boolean));
        grid.dataset.filteredCount = String(totalHits());
        if (count) count.textContent = countText(totalHits());
        if (applyCount) applyCount.textContent = countText(totalHits());
        if (actions) actions.hidden = false;
        getListControls().render(pageState.response?.primaryList);
        renderPagination();
        grid.removeAttribute('aria-busy');
        collection.classList.remove('processing');
        renderRecommendations(pageState.response);
    }

    pagination?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-voyado-page]');
        if (
            !button ||
            button.disabled ||
            button.classList.contains('disabled') ||
            button.getAttribute('aria-current') === 'page'
        ) {
            return;
        }

        event.preventDefault();
        if (button.dataset.voyadoLoadMore) landingPage.loadMore();
        else landingPage.goToPage(button.dataset.voyadoPage);
    });

    landingPage.subscribe((state) => {
        pageState = state;
        render();
    });
})(window, document);
